import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Download } from "lucide-react";
import {
  desktopPlatformName,
  detectDesktopPlatform,
  type DesktopPlatform
} from "../desktop-release.ts";
import { DesktopPlatformPicker } from "../download/DesktopPlatformPicker.tsx";

const INVITATION_CODE_PATTERN = /^[A-Za-z0-9_-]{43}$/u;

export function InvitationDownloadPage() {
  const [code, setCode] = useState(readInvitationCode);
  const [detectedPlatform] = useState(() => detectDesktopPlatform());
  const [platform, setPlatform] = useState<DesktopPlatform>(
    () => detectedPlatform ?? "macos-arm64"
  );

  useEffect(() => {
    const acceptFragmentNavigation = () => {
      if (!window.location.hash) {
        return;
      }
      setCode(readInvitationCode());
      clearInvitationFragment();
    };
    window.addEventListener("hashchange", acceptFragmentNavigation);
    clearInvitationFragment();
    return () => window.removeEventListener("hashchange", acceptFragmentNavigation);
  }, []);

  const valid = code !== null;
  const downloadUrl = valid
    ? `/v1/account-invitations/download?${new URLSearchParams({ code, platform }).toString()}`
    : null;

  return (
    <main className="invitation-page">
      <header className="invitation-brand" aria-label="Matterline">
        <img src="/matterline.svg" alt="" />
        <span>Matterline</span>
      </header>

      <section className="invitation-content" aria-labelledby="invitation-title">
        <div className="invitation-product-mark">
          <img src="/matterline.svg" alt="Matterline" />
          <span className={`invitation-status-badge ${valid ? "is-ready" : "is-invalid"}`}>
            {valid ? <CheckCircle2 aria-hidden="true" /> : <AlertCircle aria-hidden="true" />}
          </span>
        </div>
        <p className="invitation-eyebrow">{valid ? "账号已准备好" : "邀请不可用"}</p>
        <h1 id="invitation-title">{valid ? "下载 Matterline" : "这个邀请链接无效"}</h1>
        <p className="invitation-description">
          {valid
            ? "安装桌面客户端，然后使用邀请邮件中的账号设置入口完成登录。"
            : "链接可能已使用、已过期或已被新邀请替换。请联系管理员重新发送邀请邮件。"}
        </p>
        {downloadUrl ? (
          <>
            <DesktopPlatformPicker
              value={platform}
              detectedPlatform={detectedPlatform}
              onChange={setPlatform}
            />
            <a className="invitation-download" href={downloadUrl} download>
              <Download aria-hidden="true" />
              下载 {desktopPlatformName(platform)} 版
            </a>
          </>
        ) : null}
      </section>

      <footer className="invitation-footer">
        Matterline Desktop · macOS Apple silicon / Windows x64
      </footer>
    </main>
  );
}

function readInvitationCode(): string | null {
  const code = new URLSearchParams(window.location.hash.slice(1)).get("code")?.trim() ?? "";
  return INVITATION_CODE_PATTERN.test(code) ? code : null;
}

function clearInvitationFragment(): void {
  if (window.location.hash) {
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  }
}
